import React, { useState, useEffect, useGlobal } from "reactn";
import axios from "axios";
import SearchResult from "./SearchResult";
import SearchCategory from "./SearchCategory";
import "../../assets/css/componentSpecificCss/searchResultsList.css";

export const SearchResultsList = ({
  results,
  searchValue,
  fetchOrganizerData
}) => {
  const [partner] = useGlobal("dsUser");

  const [manage, setManage] = useState([]);
  const [listings, setListings] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [fileUploads, setFileUploads] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = React.useRef(0);
  const resourcesRef = React.useRef(null);
  const resourcesLoadedAtRef = React.useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    setError(false);
    setManage([]);
    setListings([]);
    setEnrollments([]);
    setLeads([]);
    setFileUploads([]);

    if (searchValue.length === 0) {
      setLoading(false);
      setIsEmpty(false);
      return;
    }

    if (!fetchOrganizerData) {
      setLoading(true);
      return;
    }

    // Matching people can be rendered while their supplementary data loads.
    setLoading(results.length === 0);
    fetchData(requestId);
  }, [searchValue, fetchOrganizerData, results]);

  const fetchData = async requestId => {
    try {
      const resourceResults = await fetchListingsData(searchValue);

      if (requestId !== requestIdRef.current) {
        return;
      }

      setManage(resourceResults.manage);
      setListings(resourceResults.listings);
      setIsEmpty(
        results.length +
          resourceResults.manage.length +
          resourceResults.listings.length ===
          0
      );
      setLoading(false);

      const canReadConnections =
        !partner?.accesspoints ||
        partner?.accesspoints?.Contacts?.Connections?.read;

      if (!canReadConnections || results.length === 0) {
        return;
      }

      const details = await Promise.all(
        results.map(user => fetchUserDetails(user))
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      setEnrollments(details.map(detail => detail.enrollments));
      setLeads(details.map(detail => detail.leads));
      setFileUploads(details.map(detail => detail.uploads));
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setIsEmpty(results.length === 0);
        setError(true);
      }
    }
  };

  const fetchUserId = async email => {
    const ep = `${process.env.REACT_APP_API}/partners/searchbar/user/${encodeURIComponent(
      email
    )}`;
    const result_ep = await axios.get(ep);

    return result_ep.data.data.length > 0 ? result_ep.data.data[0].id : -1;
  };

  const fetchListingsData = async searchValue => {
    const access = partner?.accesspoints;

    const cacheExpired = Date.now() - resourcesLoadedAtRef.current > 60000;

    if (!resourcesRef.current || cacheExpired) {
      const sharedEp = `${process.env.REACT_APP_API}/partners/searchbar/shared/${partner.id}`;
      const manageEp = `${process.env.REACT_APP_API}/partners/searchbar/manage/${partner.id}`;
      const listingsEp = `${process.env.REACT_APP_API}/partners/searchbar/listings/${partner.id}`;

      resourcesRef.current = Promise.all([
        axios.get(sharedEp),
        axios.get(manageEp),
        axios.get(listingsEp)
      ])
        .then(resourceResponses => {
          resourcesLoadedAtRef.current = Date.now();
          return resourceResponses;
        })
        .catch(requestError => {
          resourcesRef.current = null;
          throw requestError;
        });
    }

    const [sharedData, manageData, listingsData] = await resourcesRef.current;

    const filteredSharedListings = [];
    const filteredSharedManage = [];

    sharedData.data.data.forEach(shared => {
      if (
        !shared.displayName ||
        !shared.displayName
          .toLowerCase()
          .replace(/\s/g, "")
          .includes(searchValue)
      ) {
        return;
      }

      if (!access) {
        filteredSharedListings.push(shared);
        filteredSharedManage.push(shared);
      } else if (shared.classificationType === "gift_cards") {
        if (access.Listings && access.Listings["Gift Card"]?.read) {
          filteredSharedListings.push(shared);
        }

        if (access.Manage && access.Manage["Gift Card"]?.read) {
          filteredSharedManage.push(shared);
        }
      } else if (shared.classificationType === "programs") {
        if (access.Listings && access.Listings["Programs"]?.read) {
          filteredSharedListings.push(shared);
        }

        if (access.Manage && access.Manage["Programs"]?.read) {
          filteredSharedManage.push(shared);
        }
      } else if (shared.classificationType === "online") {
        if (access.Listings && access.Listings["Online Virtual"]?.read) {
          filteredSharedListings.push(shared);
        }
      } else if (shared.classificationType === "events") {
        if (access.Listings && access.Listings["Events"]?.read) {
          filteredSharedListings.push(shared);
        }

        if (access.Manage && access.Manage["Events"]?.read) {
          filteredSharedManage.push(shared);
        }
      } else if (shared.classificationType === "memberships") {
        if (access.Listings && access.Listings["Memberships"]?.read) {
          filteredSharedListings.push(shared);
        }

        if (access.Manage && access.Manage["Memberships"]?.read) {
          filteredSharedManage.push(shared);
        }
      }

    });

    const filteredManage = manageData.data.data.filter(manage => {
      if (
        !manage.displayName
          .toLowerCase()
          .replace(/\s/g, "")
          .includes(searchValue)
      ) {
        return false;
      }

      if (!access) {
        return true;
      } else if (manage.classificationType === "resources") {
        if (access.Manage && access.Manage["Resources"]?.read) {
          return access.Manage["Resources"].read
            ? manage.displayName
                .toLowerCase()
                .replace(/\s/g, "")
                .includes(searchValue)
            : false;
        }
      }
      return false;
    });

    const filteredListings = listingsData.data.data.filter(listing => {
      if (
        !listing.displayName
          .toLowerCase()
          .replace(/\s/g, "")
          .includes(searchValue)
      ) {
        return false;
      }

      if (!access) {
        return true;
      } else if (listing.classificationType === "news-updates") {
        if (access.Listings && access.Listings["News & Updates"]) {
          return access.Listings["News & Updates"].read
            ? listing.displayName
                .toLowerCase()
                .replace(/\s/g, "")
                .includes(searchValue)
            : false;
        }
      } else if (listing.classificationType === "forms") {
        if (access.Listings && access.Listings["Forms"]) {
          return access.Listings["Forms"].read
            ? listing.displayName
                .toLowerCase()
                .replace(/\s/g, "")
                .includes(searchValue)
            : false;
        }
      } else if (listing.classificationType === "promotion") {
        if (access.Listings && access.Listings["Promotions"]) {
          return access.Listings["Promotions"].read
            ? listing.displayName
                .toLowerCase()
                .replace(/\s/g, "")
                .includes(searchValue)
            : false;
        }
      }

      return false;
    });

    return {
      manage: [...filteredSharedManage, ...filteredManage],
      listings: [...filteredSharedListings, ...filteredListings]
    };
  };

  const fetchUserDetails = async user => {
    const dsUserId = user.dsUserId || (await fetchUserId(user.email));

    if (dsUserId < 0) {
      return { enrollments: [], leads: [], uploads: [] };
    }

    const enrollmentsEp = `${process.env.REACT_APP_API}/partners/searchbar/enrollments/${dsUserId}`;
    const leadsEp = `${process.env.REACT_APP_API}/partners/searchbar/leads/${dsUserId}`;
    const uploadsEp = `${process.env.REACT_APP_API}/partners/milestones_submissions/get/all/${dsUserId}`;

    const [enrollmentsData, leadsData, uploadsData] = await Promise.all([
      axios.get(enrollmentsEp, { params: { partnerId: partner.id } }),
      axios.get(leadsEp, {
        params: { partnerId: partner.id, email: user.email }
      }),
      axios.get(uploadsEp)
    ]);

    const uploads = [];
    (uploadsData.data.Participants || []).forEach(item => {
      if (item && item.file_uploads) {
        let count = 1;
        while (item.file_uploads["file_" + count]) {
          if (!item.file_uploads["file_" + count]?.isHidden) {
            uploads.push({
              ...item,
              file_uploads: item.file_uploads["file_" + count]
            });
          }
          count++;
        }
      }
    });

    uploads.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    return {
      enrollments: enrollmentsData.data.data.slice(0, 5),
      leads: leadsData.data.data.slice(0, 5),
      uploads: uploads.slice(0, 5)
    };
  };

  if (searchValue.length === 0) {
    return null;
  }

  return (
    <div className="results-list">
      {loading && (
        <div className="search-status-message">
          <SearchResult type="Loading" />
        </div>
      )}
      {!loading && error && (
        <SearchResult type="Error" />
      )}
      {!loading && !error && isEmpty && searchValue.length > 0 && (
        <SearchResult type="Empty" />
      )}
      {!loading && !isEmpty && (
        <>
          {manage.length > 0 && <SearchCategory category={"Manage"} />}
          {manage.length > 0 &&
            manage
              .sort((a, b) => {
                const nameA =
                  a && a.displayName ? a.displayName.toUpperCase() : "";
                const nameB =
                  b && b.displayName ? b.displayName.toUpperCase() : "";
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
                return 0;
              })
              .map(item => {
                return <SearchResult type={"Manage"} result={item} />;
              })}
          {listings.length > 0 && <SearchCategory category={"Listings"} />}
          {listings.length > 0 &&
            listings
              .sort((a, b) => {
                const nameA =
                  a && a.displayName ? a.displayName.toUpperCase() : "";
                const nameB =
                  b && b.displayName ? b.displayName.toUpperCase() : "";
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
                return 0;
              })
              .map(item => {
                return <SearchResult type={"Listings"} result={item} />;
              })}
          {[...results]
            .sort((a, b) => {
              const nameA = a.displayName.toUpperCase(); // Convert to uppercase for case-insensitive sorting
              const nameB = b.displayName.toUpperCase();
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              return 0;
            })
            .map((result, index) => {
              const userEnrollmentsInfo = [];
              enrollments.map(item => {
                item.forEach(enrollmentInfo => {
                  if (enrollmentInfo.user_email === result.email) {
                    userEnrollmentsInfo.push(enrollmentInfo);
                  }
                });
              });

              const userLeadsInfo = [];
              leads.map(item => {
                item.forEach(leadInfo => {
                  if (leadInfo.user_email === result.email) {
                    userLeadsInfo.push(leadInfo);
                  }
                });
              });

              const userAttachmentsInfo = [];
              fileUploads.map(item => {
                item.forEach(attachmentInfo => {
                  if (attachmentInfo.user_email === result.email) {
                    userAttachmentsInfo.push(attachmentInfo);
                  }
                });
              });

              return (
                <>
                  <SearchCategory
                    category={"User"}
                    user={result.displayName}
                    isSearchByChild={result.isSearchByChild}
                  />
                  <SearchCategory category={"Profile"} />
                  <SearchResult type={"Profile"} result={result} />
                  {(!partner?.accesspoints ||
                    (partner?.accesspoints?.Leads &&
                      partner?.accesspoints?.Leads["Appointments"] &&
                      partner?.accesspoints?.Leads["Applications"] &&
                      partner?.accesspoints?.Leads["Form Leads"])) &&
                    userLeadsInfo.length > 0 &&
                    userLeadsInfo.some(
                      lead => lead.status_stage === "New_Pending"
                    ) && <SearchCategory category={"Leads"} />}
                  {(!partner?.accesspoints ||
                    (partner?.accesspoints?.Leads &&
                      partner?.accesspoints?.Leads["Appointments"] &&
                      partner?.accesspoints?.Leads["Applications"] &&
                      partner?.accesspoints?.Leads["Form Leads"])) &&
                    userLeadsInfo.length > 0 &&
                    userLeadsInfo.some(
                      lead => lead.status_stage === "New_Pending"
                    ) && <SearchResult type={"Leads"} result={userLeadsInfo} />}
                  <SearchCategory category={"History"} />
                  <SearchResult type={"History"} result={result} />
                  <SearchCategory category={"Financials"} />
                  <SearchResult type={"Financials"} result={result} />
                  {(!partner?.accesspoints ||
                    (partner?.accesspoints?.Manage &&
                      partner?.accesspoints?.Manage["Programs"]?.read)) &&
                    userEnrollmentsInfo.length > 0 && (
                      <SearchCategory category={"Enrollments"} />
                    )}
                  {(!partner?.accesspoints ||
                    (partner?.accesspoints?.Manage &&
                      partner?.accesspoints?.Manage["Programs"]?.read)) &&
                    userEnrollmentsInfo.length > 0 && (
                      <SearchResult
                        key={index}
                        type={"Enrollments"}
                        result={userEnrollmentsInfo}
                        childrens={result.childrens}
                      />
                    )}
                  {userAttachmentsInfo.length > 0 && (
                    <SearchCategory category={"Attachments"} />
                  )}
                  {userAttachmentsInfo.length > 0 && (
                    <SearchResult
                      key={index}
                      type={"Attachments"}
                      result={userAttachmentsInfo}
                    />
                  )}
                  {(!partner?.accesspoints ||
                    (partner?.accesspoints?.Leads &&
                      partner?.accesspoints?.Leads["Appointments"]?.read &&
                      partner?.accesspoints?.Leads["Applications"]?.read &&
                      partner?.accesspoints?.Leads["Form Leads"]?.read)) &&
                    userLeadsInfo.length > 0 &&
                    !userLeadsInfo.some(
                      lead => lead.status_stage === "New_Pending"
                    ) && <SearchCategory category={"Leads"} />}
                  {(!partner?.accesspoints ||
                    (partner?.accesspoints?.Leads &&
                      partner?.accesspoints?.Leads["Appointments"]?.read &&
                      partner?.accesspoints?.Leads["Applications"]?.read &&
                      partner?.accesspoints?.Leads["Form Leads"]?.read)) &&
                    userLeadsInfo.length > 0 &&
                    !userLeadsInfo.some(
                      lead => lead.status_stage === "New_Pending"
                    ) && <SearchResult type={"Leads"} result={userLeadsInfo} />}
                </>
              );
            })}
        </>
      )}
    </div>
  );
};
